import { useCallback, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { parseDateOnly } from '@/utils/dateUtils';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useLocalStorageValue } from '@react-hookz/web';

import Page from '@/components/page/Page';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { DateRangePicker } from '@/components/common/date-pickers/date-range-picker';
import { DateRange } from 'react-day-picker';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { ClockIcon } from '@phosphor-icons/react';
import { SystemEntityInline } from '@/components/system-entities';
import { SystemEntity } from '@erp/shared-types';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';

import { useAPInvoicesList } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import type { APInvoice } from '@erp/rpc';
import { ForegroundAgentChat } from '@/components/common/ForegroundAgentChat';
import { InvoiceStatusBadge } from '@/components/accounts-payable/shared/InvoiceStatusBadge';

const INVOICE_STATUS_OPTIONS: MultiSelectOption[] = [
  { id: 'DRAFT', label: 'Draft' },
  { id: 'MATCHED', label: 'Matched' },
  { id: 'FINALIZED', label: 'Finalized' },
];

const PAYMENT_STATUS_OPTIONS: MultiSelectOption[] = [
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'partially_paid', label: 'Partially Paid' },
  { id: 'paid', label: 'Paid' },
];

type InvoiceRow = APInvoice;

const columns: ColumnDef<InvoiceRow>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Imported',
    cell: ({ getValue }) => {
      const date = getValue<string>();
      return (
        <span className="text-sm text-muted-foreground">
          {date ? format(new Date(date), 'MM/dd/yyyy h:mm a') : '—'}
        </span>
      );
    },
    size: 160,
  },
  {
    accessorKey: 'invoiceNumber',
    header: 'Invoice #',
    cell: ({ getValue }) => (
      <span className="font-medium text-sm">{getValue<string>()}</span>
    ),
    size: 160,
  },
  {
    accessorKey: 'invoiceDate',
    header: 'Invoice Date',
    cell: ({ getValue }) => {
      const dateStr = getValue<string>();
      // invoiceDate is a DATEONLY field - use parseDateOnly to avoid timezone shifts
      const date = dateStr ? parseDateOnly(dateStr) : null;
      return (
        <span className="text-sm text-muted-foreground">
          {date ? format(date, 'MM/dd/yyyy') : '—'}
        </span>
      );
    },
    size: 140,
  },
  {
    accessorKey: 'dueDate',
    header: 'Due Date',
    cell: ({ getValue, row }) => {
      const dateStr = getValue<string>();
      // dueDate is a DATEONLY field - use parseDateOnly to avoid timezone shifts
      const dueDate = dateStr ? parseDateOnly(dateStr) : null;

      // For past due comparison, compare the date as local date against today
      // Use start of today (midnight) to compare dates properly
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const pastDue =
        dueDate &&
        dueDate.getTime() < today.getTime() &&
        row.original.paymentStatus !== 'paid';

      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {dueDate ? format(dueDate, 'MM/dd/yyyy') : '—'}
          </span>
          {pastDue && (
            <Tooltip>
              <TooltipTrigger asChild>
                <ClockIcon className="h-4 w-4 text-amber-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Past Due</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
    size: 160,
  },
  {
    accessorKey: 'totalAmount',
    header: 'Total',
    cell: ({ row }) => {
      const amount = Number(row.original.totalAmount ?? '0');
      const currency = row.original.currency || 'USD';

      try {
        const validAmount = Number.isFinite(amount) ? amount : 0;
        return (
          <span className="text-sm font-semibold">
            {validAmount.toLocaleString(undefined, {
              style: 'currency',
              currency,
              minimumFractionDigits: 2,
            })}
          </span>
        );
      } catch {
        return <span className="text-sm font-semibold">$0.00</span>;
      }
    },
    size: 160,
  },
  {
    accessorKey: 'supplierId',
    header: 'Vendor',
    cell: ({ getValue }) => {
      const supplierId = getValue<string>();
      return supplierId ? (
        <div className="max-w-[200px]">
          <SystemEntityInline
            entityType={SystemEntity.AP_SUPPLIER}
            entityId={supplierId}
          />
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      );
    },
  },
  {
    accessorKey: 'referenceIds',
    header: 'Reference',
    cell: ({ row }) => {
      const referenceIds = row.original.referenceIds;
      const legacyReferenceId = row.original.referenceId;

      const allRefs = referenceIds?.length
        ? referenceIds
        : legacyReferenceId
          ? [legacyReferenceId]
          : [];

      if (allRefs.length === 0) {
        return <span className="text-sm text-muted-foreground">—</span>;
      }

      if (allRefs.length === 1) {
        return <span className="text-sm font-medium">{allRefs[0]}</span>;
      }

      return (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-sm font-medium hover:underline cursor-pointer flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {allRefs[0]}
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                +{allRefs.length - 1}
              </Badge>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2">
            <div className="text-sm font-medium mb-2">References</div>
            <ul className="space-y-1">
              {allRefs.map((ref, idx) => (
                <li key={idx} className="text-sm">
                  {ref}
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      );
    },
    size: 140,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <InvoiceStatusBadge invoice={row.original} />,
    size: 160,
  },

  {
    accessorKey: 'paymentStatus',
    header: 'Payment Status',
    cell: ({ getValue }) => {
      const status = getValue<string>();
      const variant =
        status === 'paid'
          ? 'default'
          : status === 'partially_paid'
            ? 'secondary'
            : 'outline';

      return <Badge variant={variant}>{status.replace('_', ' ')}</Badge>;
    },
    size: 160,
  },
];

// Search params type from route validation
type InvoicesSearchParams = {
  invoiceNumber?: string;
  supplierId?: string;
  status?: string[];
  paymentStatus?: string[];
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  page?: number;
  pageSize?: number;
};

const DEFAULT_PAGE_SIZE = 25;

export function InvoicesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDetailView = location.pathname !== '/accounts-payable/invoices';
  const searchParams = useSearch({
    from: '/authenticated/accounts-payable/invoices',
  }) as InvoicesSearchParams;

  const { value: savedPageSize, set: setSavedPageSize } =
    useLocalStorageValue<number>('invoices-page-size', {
      defaultValue: DEFAULT_PAGE_SIZE,
    });

  const filters = useMemo(
    () => ({
      invoiceNumber: searchParams.invoiceNumber ?? '',
      supplierId: searchParams.supplierId ?? '',
      status: searchParams.status ?? [],
      paymentStatus: searchParams.paymentStatus ?? [],
      invoiceDateRange:
        searchParams.invoiceDateFrom || searchParams.invoiceDateTo
          ? ({
              from: searchParams.invoiceDateFrom
                ? parseISO(searchParams.invoiceDateFrom)
                : undefined,
              to: searchParams.invoiceDateTo
                ? parseISO(searchParams.invoiceDateTo)
                : undefined,
            } as DateRange)
          : undefined,
      dueDateRange:
        searchParams.dueDateFrom || searchParams.dueDateTo
          ? ({
              from: searchParams.dueDateFrom
                ? parseISO(searchParams.dueDateFrom)
                : undefined,
              to: searchParams.dueDateTo
                ? parseISO(searchParams.dueDateTo)
                : undefined,
            } as DateRange)
          : undefined,
    }),
    [searchParams],
  );

  const paginationState = useMemo(() => {
    return {
      pageIndex: (searchParams.page ?? 1) - 1,
      pageSize: searchParams.pageSize ?? savedPageSize ?? DEFAULT_PAGE_SIZE,
    };
  }, [searchParams.page, searchParams.pageSize, savedPageSize]);

  // Helper to update URL search params
  const updateSearchParams = useCallback(
    (updates: Partial<InvoicesSearchParams>, resetPage = false) => {
      const newSearch = {
        ...searchParams,
        ...updates,
        // Reset to page 1 when filters change
        page: resetPage ? undefined : (updates.page ?? searchParams.page),
      };
      navigate({
        to: '/accounts-payable/invoices',
        search: newSearch,
        replace: true,
      });
    },
    [navigate, searchParams],
  );

  const queryFilters = useMemo(() => {
    return {
      invoiceNumber: filters.invoiceNumber || undefined,
      supplierId: filters.supplierId || undefined,
      status: filters.status.length > 0 ? filters.status : undefined,
      paymentStatus:
        filters.paymentStatus.length > 0 ? filters.paymentStatus : undefined,
      invoiceDateFrom: searchParams.invoiceDateFrom,
      invoiceDateTo: searchParams.invoiceDateTo,
      dueDateFrom: searchParams.dueDateFrom,
      dueDateTo: searchParams.dueDateTo,
      limit: paginationState.pageSize,
      offset: paginationState.pageIndex * paginationState.pageSize,
    };
  }, [filters, searchParams, paginationState]);

  const { invoices, pagination, isLoading } = useAPInvoicesList(queryFilters);

  const rowCount = pagination?.total ?? 0;
  const pageCount =
    rowCount > 0 ? Math.ceil(rowCount / paginationState.pageSize) : 0;

  const table = useReactTable({
    columns,
    data: invoices,
    manualPagination: true,
    rowCount,
    state: {
      pagination: paginationState,
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === 'function' ? updater(paginationState) : updater;
      if (newState.pageSize !== savedPageSize) {
        setSavedPageSize(newState.pageSize);
      }
      updateSearchParams({
        page: newState.pageIndex + 1,
        pageSize: newState.pageSize,
      });
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const onRowClick = useCallback(
    (invoice: InvoiceRow) => {
      navigate({
        to: '/accounts-payable/invoices/$invoiceId',
        params: { invoiceId: invoice.id },
        search: true,
        viewTransition: true,
      });
    },
    [navigate],
  );

  const handleFilterChange = useCallback(
    (updates: {
      invoiceNumber?: string;
      supplierId?: string;
      status?: string[];
      paymentStatus?: string[];
      invoiceDateFrom?: string;
      invoiceDateTo?: string;
      dueDateFrom?: string;
      dueDateTo?: string;
    }) => {
      updateSearchParams(updates, true); // Reset to page 1 when filters change
    },
    [updateSearchParams],
  );

  const handleInvoiceDateChange = useCallback(
    (dateRange: DateRange | undefined) => {
      handleFilterChange({
        invoiceDateFrom: dateRange?.from
          ? format(dateRange.from, 'yyyy-MM-dd')
          : undefined,
        invoiceDateTo: dateRange?.to
          ? format(dateRange.to, 'yyyy-MM-dd')
          : undefined,
      });
    },
    [handleFilterChange],
  );

  const handleDueDateChange = useCallback(
    (dateRange: DateRange | undefined) => {
      handleFilterChange({
        dueDateFrom: dateRange?.from
          ? format(dateRange.from, 'yyyy-MM-dd')
          : undefined,
        dueDateTo: dateRange?.to
          ? format(dateRange.to, 'yyyy-MM-dd')
          : undefined,
      });
    },
    [handleFilterChange],
  );

  const handleClearFilters = useCallback(() => {
    navigate({
      to: '/accounts-payable/invoices',
      search: {}, // Clear all search params
      replace: true,
    });
  }, [navigate]);

  const handlePageChange = useCallback(
    (pageIndex: number) => {
      updateSearchParams({
        page: pageIndex + 1, // Convert 0-based to 1-based
      });
    },
    [updateSearchParams],
  );

  const isFiltered = useMemo(() => {
    return Boolean(
      filters.invoiceNumber ||
        filters.supplierId ||
        filters.status.length > 0 ||
        filters.paymentStatus.length > 0 ||
        filters.invoiceDateRange ||
        filters.dueDateRange,
    );
  }, [filters]);

  if (isDetailView) {
    return <Outlet />;
  }

  return (
    <Page title="Invoices">
      <div className="space-y-4">
        <Card className="p-4 space-y-4">
          <DataTableToolbar
            table={table}
            onClearFilters={handleClearFilters}
            hasCustomFilters={isFiltered}
          >
            <Input
              placeholder="Invoice #"
              value={filters.invoiceNumber}
              onChange={(event) =>
                handleFilterChange({
                  invoiceNumber: event.target.value || undefined,
                })
              }
              className="h-8 w-[140px]"
            />
            <SupplierAutocomplete
              supplierId={filters.supplierId || undefined}
              onSelect={(supplierId) =>
                handleFilterChange({ supplierId: supplierId || undefined })
              }
              className="w-[200px]"
            />
            <MultiSelect
              options={INVOICE_STATUS_OPTIONS}
              value={INVOICE_STATUS_OPTIONS.filter((o) =>
                filters.status.includes(o.id),
              )}
              onChange={(options) => {
                const ids = options.map((o) => o.id);
                handleFilterChange({
                  status: ids.length > 0 ? ids : undefined,
                });
              }}
              placeholder="Status"
              size="sm"
              shouldAutoSort={false}
              className="h-8"
            />
            <MultiSelect
              options={PAYMENT_STATUS_OPTIONS}
              value={PAYMENT_STATUS_OPTIONS.filter((o) =>
                filters.paymentStatus.includes(o.id),
              )}
              onChange={(options) => {
                const ids = options.map((o) => o.id);
                handleFilterChange({
                  paymentStatus: ids.length > 0 ? ids : undefined,
                });
              }}
              placeholder="Payment Status"
              size="sm"
              shouldAutoSort={false}
              className="h-8"
            />
            <DateRangePicker
              value={filters.invoiceDateRange}
              onChange={handleInvoiceDateChange}
              placeholder="Invoice date"
              className="w-fit"
            />
            <DateRangePicker
              value={filters.dueDateRange}
              onChange={handleDueDateChange}
              placeholder="Due date"
              className="w-fit"
            />
          </DataTableToolbar>
        </Card>

        <Card>
          <DataTable
            table={table}
            isLoading={isLoading}
            onRowClick={(row) => onRowClick(row)}
          />
          <DataTablePagination
            table={table}
            pageCount={pageCount}
            pageIndex={paginationState.pageIndex}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      {location.pathname === '/accounts-payable/invoices' && (
        <ForegroundAgentChat
          moduleKey="ap"
          currentPage="/accounts-payable/invoices"
        />
      )}
    </Page>
  );
}
